import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const getApiUrl = () =>
  window.location.hostname.includes('vireo-renovation.fr')
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem('vireo_chat_session');
  if (!id) {
    id = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('vireo_chat_session', id);
  }
  return id;
};

const WELCOME = "Bonjour ! Je suis l'assistant VIREO. Parlez-moi de votre projet de rénovation (appartement, maison, immeuble, commerce...) et je vous guide vers un devis gratuit.";

const SUGGESTIONS = [
  'Rénover mon appartement',
  'Ravalement de façade',
  'Je suis syndic / architecte'
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const interacted = useRef(false);

  useEffect(() => {
    if (open && !loaded) {
      // Show the welcome bubble immediately so it is never lost on a fast first send
      setMessages((prev) => (prev.length ? prev : [{ role: 'assistant', content: WELCOME }]));
      fetch(`${getApiUrl()}/api/chat/history/${sessionId.current}`)
        .then((r) => r.json())
        .then((data) => {
          // Do not overwrite messages if the user has already started chatting
          // (avoids a race where history resolves after the first optimistic send).
          if (!interacted.current) {
            const history = (data.messages || []).filter((m) => m.content);
            if (history.length) setMessages(history);
          }
          setLoaded(true);
        })
        .catch(() => {
          setLoaded(true);
        });
    }
  }, [open, loaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    interacted.current = true;
    setInput('');
    setSending(true);
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`${getApiUrl()}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: trimmed })
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();
        for (const evt of events) {
          if (!evt.startsWith('data: ')) continue;
          const data = evt.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: 'assistant',
                  content: next[next.length - 1].content + parsed.delta
                };
                return next;
              });
            } else if (parsed.error) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: parsed.error };
                return next;
              });
            }
          } catch (e) { /* skip malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Erreur de connexion. Réessayez ou appelez-nous au 01 85 09 71 25.'
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const goToContact = () => {
    setOpen(false);
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else window.location.href = '/#contact';
  };

  const showSuggestions = messages.length <= 1 && !sending;

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-40 right-5 z-50 w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ height: 'min(540px, 70vh)' }}
        >
          <div className="bg-slate-900 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                <MessageCircle className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">Assistant VIREO</p>
                <p className="text-emerald-400 text-xs">Rénovation & devis gratuit</p>
              </div>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-sm'
                      : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                  }`}
                >
                  {m.content || <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
                </div>
              </div>
            ))}
            {showSuggestions && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                    onClick={() => sendMessage(s)}
                    className="text-xs bg-white border border-emerald-300 text-emerald-700 rounded-full px-3 py-1.5 hover:bg-emerald-50 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white p-3">
            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
                placeholder="Votre message..."
                className="flex-1 text-sm border border-slate-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <Button
                data-testid="chat-send-button"
                onClick={() => sendMessage(input)}
                disabled={sending || !input.trim()}
                size="icon"
                className="bg-emerald-600 hover:bg-emerald-700 rounded-full flex-shrink-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <button
              data-testid="chat-cta-devis"
              onClick={goToContact}
              className="w-full mt-2 text-xs text-emerald-700 hover:text-emerald-800 font-medium transition-colors"
            >
              Demander un devis gratuit →
            </button>
          </div>
        </div>
      )}

      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-5 z-50 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105"
        aria-label="Ouvrir le chat VIREO"
      >
        {open ? <X className="h-6 w-6 text-white" /> : <MessageCircle className="h-6 w-6 text-white" />}
      </button>
    </>
  );
};

export default ChatWidget;
